import React, { useRef, useState } from "react";
import styled from "styled-components";
import { motion, useScroll, useTransform } from "framer-motion";
import { Tagline } from "./WhoWeAre";

const data = [
  {
    wTitle: "Real-World Awareness Approach",
    wDesc:
      "On-ground initiatives and direct interactions simplify insurance beyond digital screens.",
    oTitle: "Primarily Digital Experience",
    oDesc: "Mostly focused on online tools and automated processes.",
  },
  {
    wTitle: "Understanding Before Decisions",
    wDesc:
      "Focus on helping users understand concepts clearly before taking any step.",
    oTitle: "Decision-Oriented Journeys",
    oDesc: "Users are guided quickly through predefined options.",
  },
  {
    wTitle: "Human-Centered Guidance",
    wDesc: "Built around real conversations and practical explanations.",
    oTitle: "System-Driven Interactions",
    oDesc: "Structured and process-based interactions.",
  },
  {
    wTitle: "Ongoing Support with Wealthies Shield",
    wDesc: "Continued guidance for claims, renewals, and beyond.",
    oTitle: "Limited Post-Process Support",
    oDesc: "Support scope may vary after initial stages.",
  },
  {
    wTitle: "Accessible & Pressure-Free",
    wDesc: "Explore guidance without unnecessary pressure.",
    oTitle: "Structured Conversion Flows",
    oDesc: "Designed around predefined user journeys.",
  },
];

const Comparison = () => {
  const ref = useRef(null);
  const scrollRef = useRef(null);
  const [active, setActive] = useState(0);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start center", "end end"],
  });

  const lineHeight = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  const handleScroll = () => {
    const scrollLeft = scrollRef.current.scrollLeft;
    const width = scrollRef.current.clientWidth;
    const index = Math.round(scrollLeft / width);
    setActive(index);
  };

  return (
    <Section ref={ref}>
      {/* HEADER */}
      <div style={{ textAlign: "center", marginBottom: "40px" }}>
        <h1 style={{ marginBottom: "20px" }}>
          Why <span>we are </span>different?
        </h1>

        <Tagline>Not Just Digital. We Go Beyond the Screen.</Tagline>

        <p>
          While many platforms focus only on online tools and comparisons,
          Wealthies takes a more human approach.
        </p>

        <p>
          We actively engage with individuals through awareness initiatives,
          real conversations, and practical guidance — helping people understand
          insurance beyond just digital interfaces.
        </p>
      </div>

      {/* DESKTOP */}
      <DesktopWrapper>
        {/* <h2>
          <span>Wealthies</span> vs Other Platforms
        </h2> */}

        <Wrapper>
          {/* ✅ CENTER LINE (FIXED) */}
          <CenterLineWrapper>
            <VS>VS</VS>
            <Line style={{ height: lineHeight }} />
          </CenterLineWrapper>

          {/* HEADINGS */}
          <HeadingRow>
            <Heading>
              <span>WEALTHIES</span>
            </Heading>
            <div />
            <Heading muted>OTHER PLATFORMS</Heading>
          </HeadingRow>

          {/* ROWS */}
          {data.map((item, i) => (
            <Row key={i}>
              <Item>
                <Title>{item.wTitle}</Title>
                <Desc>{item.wDesc}</Desc>
              </Item>

              <Spacer />

              <Item>
                <Title muted>{item.oTitle}</Title>
                <Desc muted>{item.oDesc}</Desc>
              </Item>
            </Row>
          ))}
        </Wrapper>
      </DesktopWrapper>

      {/* MOBILE */}
      <MobileWrapper>
        <Carousel ref={scrollRef} onScroll={handleScroll}>
          {data.map((item, i) => (
            <Card key={i}>
              <CardTitle>
                <span>WEALTHIES</span>
              </CardTitle>
              <CardHeading>{item.wTitle}</CardHeading>
              <CardText>{item.wDesc}</CardText>

              <DividerRow>
                <LineH />
                <VSBadge>VS</VSBadge>
                <LineH />
              </DividerRow>

              <CardTitle secondary>OTHER PLATFORMS</CardTitle>
              <CardHeading secondary>{item.oTitle}</CardHeading>
              <CardText secondary>{item.oDesc}</CardText>
            </Card>
          ))}
        </Carousel>

        <Dots>
          {data.map((_, i) => (
            <Dot key={i} active={i === active} />
          ))}
        </Dots>
      </MobileWrapper>
    </Section>
  );
};

export default Comparison;

/* ---------------- STYLES ---------------- */

const Section = styled.section`
  padding: 20px 6%;
  background: #f9fafb;

  span {
    color: #005f73;
  }
`;

const DesktopWrapper = styled.div`
  margin-left: 6%;

  h2 {
    text-align: center;
    margin: 20px 0;
  }

  @media (max-width: 900px) {
    display: none;
  }
`;

const Wrapper = styled.div`
  position: relative;
`;

const HeadingRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 80px 1fr;
  margin-bottom: 20px;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 80px 1fr;
  gap: 20px;
  margin-bottom: 28px;
`;

const Spacer = styled.div``;

const Heading = styled.h3`
  font-size: 1.5rem;
  text-align: center;
  color: ${(p) => (p.muted ? "#6b7280" : "#111")};
`;

const Item = styled.div``;

const Title = styled.div`
  font-weight: ${(p) => (p.muted ? 500 : 600)};
  color: ${(p) => (p.muted ? "#6b7280" : "#111")};
`;

const Desc = styled.p`
  color: ${(p) => (p.muted ? "#9ca3af" : "#555")};
`;

/* ✅ CENTER LINE FIX */
const CenterLineWrapper = styled.div`
  position: absolute;
  left: 50%;
  top: 60px;
  transform: translateX(-50%);
  height: calc(100% - 60px);
  display: flex;
  flex-direction: column;
  align-items: center;
  pointer-events: none;
`;

const Line = styled(motion.div)`
  width: 4px;
  background: #005f73;
  transform-origin: top;
`;

const VS = styled.div`
  position: absolute;
  top: -25px;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #d1fae5;
  font-weight: 700;
  color: #005f73;
  border-radius: 50%;
`;

/* MOBILE (unchanged) */

const MobileWrapper = styled.div`
  display: none;

  @media (max-width: 900px) {
    display: block;
  }
`;

const Carousel = styled.div`
  display: flex;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  gap: 20px;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const Card = styled.div`
  min-width: 100%;
  background: #f3f4f6;
  padding: 25px;
  border-radius: 16px;
`;

const CardTitle = styled.div`
  font-size: 0.8rem;
  color: ${(p) => (p.secondary ? "#9ca3af" : "#111")};
`;

const CardHeading = styled.div`
  font-weight: 600;
  color: ${(p) => (p.secondary ? "#6b7280" : "#111")};
`;

const CardText = styled.p`
  color: ${(p) => (p.secondary ? "#9ca3af" : "#555")};
`;

const DividerRow = styled.div`
  display: flex;
  align-items: center;
  margin: 20px 0;
`;

const LineH = styled.div`
  flex: 1;
  height: 2px;
  background: #d1d5db;
`;

const VSBadge = styled.div`
  padding: 6px 12px;
  background: #e5e7eb;
  border-radius: 20px;
  font-size: 0.75rem;
`;

const Dots = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 15px;
  gap: 6px;
`;

const Dot = styled.div`
  width: ${(p) => (p.active ? "18px" : "8px")};
  height: 6px;
  background: ${(p) => (p.active ? "#888" : "#d1d5db")};
  border-radius: 10px;
`;
